import { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isHmoPaymentFlow } from '../../../utils/hmoUtils'
import { Radio, Checkbox, Button, Spin } from 'antd'
import BillingAddressSection from './BillingAddressSection'
import EditableItem from '../EditableItem'
import $t from '../../../i18n'
import ZuoraPaymentPage from '../../pages/ZuoraPaymentPage/ZuoraPaymentPage'
import { isEmpty } from 'ramda'
import Actions from '../../../actions'
import { getMemberAddressByType } from '../../../utils/addressesUtil'

import './PaymentDetailsCard.css'

const getValidationErrors = ({
  address,
  city,
  zip,
  isHomeAddressConfirmed
}) => {
  const result = {}
  if (!address) {
    result.address = $t('pages.billing.noAddress')
  }
  if (!city) {
    result.city = $t('pages.billing.noCity')
  }
  if (!zip) {
    result.zip = $t('pages.billing.noZip')
  }
  if (zip) {
    if (zip?.length !== 5) {
      result.zip = $t('pages.billing.invalidZip')
    }
  }
  if (!isHomeAddressConfirmed) {
    result.homeConfirmed = $t('errors.validation.missing')
  }
  return (!isEmpty(result) && result) || null
}

// The payment details card is used to display the payment details of the user.
// It also allows the user to edit the payment details.
// The user can also add a new payment method.
// This has three views:
// 1. manage view / editable view
// 2. Non-editable view - new payment method
// 3. Non-editable view - for non authenticated user
// Please make sure to use safe get here to avoid any errors (because of user type switching).
const PaymentDetailsCard = ({
  isEditableView,
  onEdit,
  zouraIframeRef,
  SubmitButtonText,
  onSuccess,
  onCancel,
  isManagePayment,
  unauthenticatedPaymentDetails,
  disabled,
  actionRef
}) => {
  const dispatch = useDispatch()

  const userDetails = useSelector(state => state.profile.userDetails)

  const selectedPlan = useSelector(state => state.payment.selectedPlan)

  // hmo ppayment flow is for hmo member that not selected one time plan
  // the one time plan selection is only for hmo member that is not activated insurance
  // or for telehelath that selected one time plan
  // hmo member can be also unauthenticated
  const isHmoPayment =
    isHmoPaymentFlow(userDetails, selectedPlan) ||
    !!unauthenticatedPaymentDetails

  const paymentDetailsData = useSelector(
    state => state.payment.paymentDetailsData
  )

  const isLoadingCheckoutPage = useSelector(
    state => state.payment.isLoadingCheckoutPage
  )

  const userHomeAddressState = useSelector(
    state => state.account?.userHomeAddressState
  )

  const isSubmitLoading = useSelector(state => state.payment.zouraPageLoader)

  const updatingDetailsDialogLoader = useSelector(
    state => state.payment.updatingBillingDialogLoader
  )

  const zuoraSignatureDetails = useSelector(
    state => state.payment.zuoraSignatureDetails
  )

  const billingAddress = useSelector(state => state.payment.paymentAddressObj)

  const pages = useMemo(
    () =>
      unauthenticatedPaymentDetails
        ? unauthenticatedPaymentDetails?.pages
        : // zuoraSignatureDetails can be an array of pages or an object (one page)
          zuoraSignatureDetails?.pages || [zuoraSignatureDetails],
    [unauthenticatedPaymentDetails, zuoraSignatureDetails]
  )

  const isMoreThanOneBillingOption = pages?.length > 1

  const [isAutopaySelected, setIsAutopaySelected] = useState(true)
  const [isSaveCard, setIsSaveCard] = useState(true)
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(pages?.[0])
  const [shouldValidate, setShouldValidate] = useState(false)
  const [isHomeAddressConfirmed, setIsHomeAddressConfirmed] = useState(
    !isHmoPayment
  )

  useEffect(() => {
    setSelectedPaymentOption(pages?.[0])
  }, [pages])

  const isCheckOptionSelected =
    selectedPaymentOption === $t('pages.paymentsAndBilling.checkOption')

  const isPaymentMethodAvailable =
    paymentDetailsData?.paymentMethod &&
    (paymentDetailsData?.paymentMethod?.cardNumber ||
      paymentDetailsData?.paymentMethod?.bankAccountNumber)

  const errors =
    shouldValidate &&
    getValidationErrors({
      address: billingAddress?.creditCardAddress1,
      city: billingAddress?.creditCardCity,
      zip: billingAddress?.creditCardPostalCode,
      isHomeAddressConfirmed
    })

  const onSubmitDetails = () => {
    const validationErrors = getValidationErrors({
      address: billingAddress?.creditCardAddress1,
      city: billingAddress?.creditCardCity,
      zip: billingAddress?.creditCardPostalCode,
      isHomeAddressConfirmed
    })
    if (validationErrors) {
      setShouldValidate(true)
      dispatch(Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER))

      return
    }
    setShouldValidate(false)

    // Check has no real iframe to post to, so the loader would spin forever
    // waiting for a response that never comes. Skip straight to onSuccess.
    if (isCheckOptionSelected) {
      onSuccess({
        paymentMethodType: selectedPaymentOption,
        autoPay: false,
        savePaymentMethod: false
      })
      return
    }

    dispatch(Actions.createAction(Actions.ENABLE_ZOURA_PAGE_LOADER))

    // find the specific iframe and submit the form
    document
      .getElementById(`paymentFrame ${selectedPaymentOption?.pageId}`)
      ?.contentWindow?.postMessage('submit', '*')
  }

  useEffect(() => {
    if (actionRef) {
      actionRef.current = { submitDetails: onSubmitDetails }
    }
  }, [onSubmitDetails])

  return (
    <>
      <EditableItem
        className='payment-details-card'
        title={$t('pages.billing.paymentMethodAndBilling')}
        hideParticipents={true}
        disableEdit={isEditableView}
        onEdit={onEdit}
      >
        {!isEditableView && (
          <div className='payment-details-sections'>
            <div className='payment-details-section'>
              <div className='section-title semi-bold'>
                {isPaymentMethodAvailable
                  ? `${$t(
                      `pages.billing.${paymentDetailsData?.paymentMethod?.type}`,
                      {
                        defaults: [
                          {
                            message: paymentDetailsData?.paymentMethod?.type
                          }
                        ]
                      }
                    )}${
                      paymentDetailsData?.accountSummary?.isAutoPay
                        ? ` - ${$t('pages.billing.paymentSummary.autopay')}`
                        : ''
                    }`
                  : $t('pages.billing.noPaymentMethod')}
              </div>
              {isPaymentMethodAvailable && (
                <div className='sectin-value'>
                  {paymentDetailsData?.paymentMethod?.cardType}{' '}
                  {$t('pages.billing.endingIn', {
                    // Show only the last 4 digits, works for both CC (cardNumber) and ACH (bankAccountNumber)
                    last4: (
                      paymentDetailsData?.paymentMethod?.cardNumber ||
                      paymentDetailsData?.paymentMethod?.bankAccountNumber
                    )?.slice(-4)
                  })}
                </div>
              )}
              {isPaymentMethodAvailable &&
                paymentDetailsData?.paymentMethod?.expirationMonth && (
                  <div className='sectin-value'>
                    {$t('pages.billing.expiryDate', {
                      date:
                        paymentDetailsData?.paymentMethod?.expirationMonth +
                        '/' +
                        paymentDetailsData?.paymentMethod?.expirationYear
                    })}
                  </div>
                )}
            </div>
            {isPaymentMethodAvailable && (
              <div className='payment-details-section'>
                <BillingAddressSection
                  isEditableView={false}
                  hideSameAsHomeAddressOption={isManagePayment}
                  locationData={paymentDetailsData?.billingAddress}
                  title={
                    isManagePayment
                      ? $t('pages.billing.addressHeading')
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        )}
        {isLoadingCheckoutPage && isEditableView && (
          <Spin size='large' className='center-spinner' />
        )}
        {isEditableView && !isLoadingCheckoutPage && (
          <div className='zoura-iframe-contaier'>
            {isMoreThanOneBillingOption && (
              <Radio.Group
                onChange={e => setSelectedPaymentOption(e?.target?.value)}
                value={selectedPaymentOption}
              >
                {pages?.map((page, i) => (
                  <Radio value={page} key={i}>
                    {$t(`pages.paymentsAndBilling.${page?.type}`)}
                    <i className={`icon-${page?.type} payment-method-icon`} />
                  </Radio>
                ))}
                {isHmoPayment && !isManagePayment && (
                  <Radio
                    value={$t('pages.paymentsAndBilling.checkOption')}
                    key={'check'}
                  >
                    {$t('pages.paymentsAndBilling.checkOption')}
                    <i className='icon-paycheck payment-method-icon' />
                  </Radio>
                )}
              </Radio.Group>
            )}
            {
              // NOTE - we need to render all pages iframe to avoid the iframe to be removed from the dom
              // we can hide the iframe using css according to the selected payment option
              pages?.map((page, i) => {
                return (
                  <ZuoraPaymentPage
                    pageDetails={page}
                    key={i}
                    isHidden={page !== selectedPaymentOption}
                    ref={zouraIframeRef}
                    onSuccessHandler={payload => {
                      onSuccess({
                        ...payload,
                        paymentMethodType: selectedPaymentOption?.type,
                        autoPay: isAutopaySelected,
                        savePaymentMethod: isSaveCard || isAutopaySelected
                      })
                    }}
                  />
                )
              })
            }
            {isCheckOptionSelected && (
              <div className='check-option-desc'>
                {$t('pages.paymentsAndBilling.checkOptionDesc', {
                  state: unauthenticatedPaymentDetails
                    ? unauthenticatedPaymentDetails?.address?.state
                    : userHomeAddressState
                })}
                <div className='semi-bold'>
                  {$t('pages.paymentsAndBilling.checkOptionDescAddress', {
                    state: unauthenticatedPaymentDetails
                      ? unauthenticatedPaymentDetails?.address?.state
                      : userHomeAddressState
                  })}
                </div>
                {$t('pages.paymentsAndBilling.checkOptionDescNote')}
              </div>
            )}
            <>
              {!isCheckOptionSelected && isHmoPayment && !isManagePayment && (
                <div className='column'>
                  <Checkbox
                    className='copay-checkbox'
                    onChange={() => setIsAutopaySelected(!isAutopaySelected)}
                    checked={isAutopaySelected}
                  >
                    {$t('pages.billing.autoPay')}
                  </Checkbox>
                  {!isAutopaySelected && (
                    <Checkbox
                      className='save-card-checkbox'
                      onChange={() => setIsSaveCard(!isSaveCard)}
                      checked={isSaveCard}
                    >
                      {$t('pages.billing.saveCard')}
                    </Checkbox>
                  )}
                </div>
              )}

              <BillingAddressSection
                isEditableView={true}
                errors={errors}
                hideSameAsHomeAddressOption={!isManagePayment}
                locationData={
                  // for tele users we need to show declare location so no need to pass the location data
                  unauthenticatedPaymentDetails
                    ? unauthenticatedPaymentDetails?.address
                    : isHmoPayment
                    ? getMemberAddressByType(userDetails, 'home')
                    : null
                }
                isHmo={isHmoPayment}
                onConfirmHomeAddress={setIsHomeAddressConfirmed}
                isHomeAddressConfirmed={isHomeAddressConfirmed}
              />
            </>
          </div>
        )}
        {isEditableView && isManagePayment && !isLoadingCheckoutPage && (
          <div className='manage-payment'>
            <Button onClick={onCancel} className='cancel-button'>
              {$t('common.cancel')}
            </Button>
            <Button
              type='primary'
              onClick={onSubmitDetails}
              loading={updatingDetailsDialogLoader || isSubmitLoading}
              className='save-button'
            >
              {$t('pages.paymentDetails.saveDetails')}
            </Button>
          </div>
        )}
      </EditableItem>
      {isEditableView &&
        SubmitButtonText &&
        !isManagePayment &&
        !isLoadingCheckoutPage && (
          <div className='paymet-details-footer'>
            <Button
              className='payment-details-submit'
              type='primary'
              loading={isSubmitLoading}
              onClick={onSubmitDetails}
              disabled={disabled}
            >
              {SubmitButtonText}
            </Button>
          </div>
        )}
    </>
  )
}

export default PaymentDetailsCard
